import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MdClose } from 'react-icons/md';
import PerfectScrollBar from 'react-perfect-scrollbar';
import { useDispatch, useSelector } from 'react-redux';

import { CircularProgress } from '@material-ui/core';
import { SubmitHandler, FormHandles } from '@unform/core';
import { Form } from '@unform/web';
import * as Yup from 'yup';

import { IState } from '~/store';
import {
  getGroupsRequest,
  getUserGroupsRequest,
  addUserGroupRequest,
  removeUserGroupRequest,
} from '~/store/modules/users/actions';
import { IGroup, IUser } from '~/store/modules/users/types';

import { Container, CustomSelect } from './styles';

interface Props {
  user: IUser;
}

interface FormData {
  group_id: string;
}

const CamundaGroups: React.FC<Props> = ({ user }) => {
  const [selectedUser, setSelectedUser] = useState<IUser | null>(null);
  const [availableGroups, setAvailableGroups] = useState<IGroup[] | null>(null);

  const loadingGroupsOrProfile = useSelector(
    (state: IState) => state.users.loadingGroupsOrProfile,
  );
  const users = useSelector((state: IState) => state.users.users);
  const groups = useSelector((state: IState) => state.users.groups);

  const formRef = useRef<FormHandles>(null);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(getGroupsRequest());
    dispatch(getUserGroupsRequest(user.id));
  }, [dispatch, user.id]);

  useEffect(() => {
    const userIndex = users.findIndex((findUser) => findUser.id === user.id);

    setSelectedUser(users[userIndex]);
  }, [user.id, users]);

  useEffect(() => {
    if (groups && selectedUser?.groups) {
      const filteredGroups = groups.filter(
        (group) =>
          group.id !==
          selectedUser.groups.find((findGroup) => findGroup.id === group.id)
            ?.id,
      );

      setAvailableGroups(filteredGroups);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, selectedUser?.groups]);

  const handleRemoveGroup = useCallback(
    (user_id, group) => {
      dispatch(removeUserGroupRequest(user_id, group));
    },
    [dispatch],
  );

  const handleSubmit: SubmitHandler<FormData> = async (data, { reset }) => {
    try {
      const schema = Yup.object().shape({
        group_id: Yup.string().required('Escolha uma função.'),
      });

      await schema.validate(data, {
        abortEarly: false,
      });

      const findedGroup = groups.find((group) => group.id === data.group_id);

      if (findedGroup === undefined) {
        formRef.current?.setFieldError('group_id', 'Grupo inválido');
        return;
      }

      dispatch(addUserGroupRequest(user.id, findedGroup));
      reset();
    } catch (err) {
      const validationErrors = {};
      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          Object.assign(validationErrors, {
            [error.path]: error.message,
          });
        });
        formRef.current?.setErrors(validationErrors);
      }
    }
  };

  return (
    <Container>
      {availableGroups && (
        <>
          <Form ref={formRef} onSubmit={handleSubmit}>
            <CustomSelect
              label="Funções"
              placeholder="Selecione uma função"
              name="group_id"
              options={availableGroups.map((group) => ({
                value: group.id,
                label: group.name,
              }))}
              classNamePrefix="react-select"
            />
            <button type="submit" disabled={loadingGroupsOrProfile}>
              {loadingGroupsOrProfile ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                'Adicionar'
              )}
            </button>
          </Form>
          <PerfectScrollBar
            style={{
              maxHeight: '200px',
              paddingRight: '15px',
              marginTop: '20px',
            }}
          >
            <table>
              <thead>
                <tr>
                  <th>Função</th>
                  <th style={{ textAlign: 'right' }}>Remover</th>
                </tr>
              </thead>
              <tbody>
                {selectedUser &&
                  selectedUser.groups &&
                  selectedUser.groups.length === 0 && (
                    <tr>
                      <td colSpan={2}>
                        <p>Nenhum grupo atribuído</p>
                      </td>
                    </tr>
                  )}
                {selectedUser &&
                  selectedUser.groups &&
                  selectedUser?.groups.map((group) => (
                    <tr key={`${group.id}-${group.name}`}>
                      <td>{group.name}</td>
                      <td style={{ textAlign: 'right' }}>
                        {selectedUser.groups.length > 1 && (
                          <button
                            type="button"
                            onClick={
                              () => handleRemoveGroup(selectedUser.id, group)
                              // eslint-disable-next-line react/jsx-curly-newline
                            }
                          >
                            {loadingGroupsOrProfile ? (
                              <CircularProgress size={20} color="inherit" />
                            ) : (
                              <MdClose size={20} />
                            )}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </PerfectScrollBar>
        </>
      )}
    </Container>
  );
};

export default CamundaGroups;
